import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, X, ArrowRight } from "lucide-react";

/**
 * GuestBanner — friendly "browsing as guest" indicator on Discovery.
 *
 * Shows guests their favorite count, encourages signup gently. Dismissible
 * for the session via sessionStorage so it doesn't nag on every page load.
 */
const KEY = "companin_guest_banner_dismissed";

export default function GuestBanner() {
  const { user } = useAuth();
  const { count } = useFavorites();
  const navigate = useNavigate();
  const [hidden, setHidden] = React.useState(() => {
    try {
      return sessionStorage.getItem(KEY) === "1";
    } catch {
      return false;
    }
  });

  if (user || hidden) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(KEY, "1");
    } catch {}
    setHidden(true);
  };

  return (
    <div
      className="relative bg-gradient-to-r from-[#4A5D4E] via-[#5C7361] to-[#4A5D4E] text-white rounded-2xl p-5 sm:p-6 mb-6 shadow-md overflow-hidden fade-in"
      data-testid="guest-banner"
    >
      {/* Decorative sparkles */}
      <div className="absolute -top-2 -right-2 opacity-15 pointer-events-none">
        <Sparkles className="h-24 w-24" strokeWidth={1.2} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative">
        <div className="flex items-start gap-3 sm:items-center">
          <div className="h-10 w-10 rounded-2xl bg-white/15 backdrop-blur grid place-items-center shrink-0">
            <Heart className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-white/70">
              Browsing as guest
            </div>
            <div className="font-display text-lg sm:text-xl mt-0.5 leading-snug">
              {count > 0
                ? `You've saved ${count} companion${count === 1 ? "" : "s"} to favourites — sign in to book or chat.`
                : "Browse all 30+ companions freely. Sign in only when you're ready to book or chat."}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={() => navigate("/auth")}
            className="rounded-full bg-white text-stone-900 hover:bg-stone-100 px-5 font-medium"
            data-testid="guest-banner-signup"
          >
            Sign up free <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
          <button
            onClick={dismiss}
            className="text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
            aria-label="Dismiss"
            data-testid="guest-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
